import React from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format, isToday, isYesterday, parseISO, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { MdOutlineLocalActivity } from "react-icons/md";
import { LuHistory } from "react-icons/lu";
import { Card, CardHeader, EmptyState, Loading, MiniTrendSparkline, PageWrapper } from "../components/blocks";
import { Apis } from "../util/Apis";
import { getTrendIndicator } from "../util/general";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDayLabel = (dateValue) => {
  const parsedDate = parseISO(dateValue);

  if (isToday(parsedDate)) {
    return "Hoy";
  }

  if (isYesterday(parsedDate)) {
    return "Ayer";
  }

  const label = format(parsedDate, "d 'de' MMMM", { locale: es });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const buildFakeCouponSummary = (availableCoupons) => {
  const base = toNumber(availableCoupons);

  return Array.from({ length: 5 }, (_, index) => ({
    date: format(subDays(new Date(), index), "yyyy-MM-dd"),
    quantity: Math.max(0, Math.floor(base / (index + 2)) + (4 - index) * 2),
  }));
};

const DetalleCupones = () => {
  const salaInfo = JSON.parse(localStorage.getItem("salaInfo"));
  const { id } = useParams();

  const { data: dataCupones, isLoading: loadingCupones } = useQuery({
    queryKey: ["point-coupons", salaInfo?.salaId],
    queryFn: async () => {
      const res = await axios.get(Apis.CAPP + "/point-coupons?salaId=" + salaInfo.salaId);
      return res.data;
    },
    enabled: !!salaInfo?.salaId,
  });

  const cuponSelect = dataCupones?.coupons?.find((item) => item.id === JSON.parse(id)) || {};
  const dailyCouponSummary = buildFakeCouponSummary(cuponSelect.available);

  const summaryWithTrend = dailyCouponSummary.map((item, index) => ({
    ...item,
    label: formatDayLabel(item.date),
    trend: getTrendIndicator(
      toNumber(item.quantity),
      index === dailyCouponSummary.length - 1 ? NaN : toNumber(dailyCouponSummary[index + 1]?.quantity)
    ),
  }));

  return (
    <PageWrapper>
      {loadingCupones ? (
        <Loading />
      ) : (
        <div className="flex flex-col w-full gap-4">
          <Card variant="dark" className="relative gap-5 overflow-hidden">
            <CardHeader
              title={cuponSelect?.couponName ?? "--"}
              subtitle="Total disponible"
              icon={<MdOutlineLocalActivity size={24} />}
              variant="dark"
            />
            <MiniTrendSparkline
              data={[...dailyCouponSummary].reverse().map((item) => item.quantity)}
              className="absolute bottom-6 right-6"
            />

            <div className="flex items-baseline gap-1 px-2 pr-24">
              <span className="text-3xl font-extrabold">{cuponSelect?.available ?? 0}</span>
              <span className="text-base text-slate-100">
                {cuponSelect?.available === 1 ? "cupon disponible" : "cupones disponibles"}
              </span>
            </div>
          </Card>

          {summaryWithTrend.length > 0 ? (
            <Card>
              <CardHeader
                title="Resumen diario"
                subtitle="Cupones acumulados por dia en los ultimos 5 dias"
                icon={<LuHistory size={24} />}
              />

              <div className="flex flex-col w-full gap-3">
                {summaryWithTrend.map((item) => (
                  <div
                    key={item.date}
                    className="flex items-center gap-3 px-4 py-3 border bg-slate-50 border-slate-100 rounded-2xl"
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-slate-950">{item.label}</span>
                      <span className="text-xs text-slate-500">Acumulado del dia</span>
                    </div>

                    <div className="flex flex-col items-end ml-auto shrink-0">
                      <div className="flex items-center gap-1">
                        <span className="text-lg font-extrabold text-slate-950">
                          {item.quantity > 0 ? `+${Math.floor(item.quantity)}` : 0}
                        </span>
                        <item.trend.icon size={15} className={item.trend.className} />
                      </div>
                      <span className="text-xs text-slate-500">
                        {item.quantity === 1 ? "cupon" : "cupones"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <EmptyState
              title="No hay informacion disponible"
              subtitle="Aun no hay un resumen diario para este cupon"
            />
          )}
        </div>
      )}
    </PageWrapper>
  );
};

export default DetalleCupones;
